import { Box, Flex, FlexProps, Text, Tooltip, Heading } from 'pounceV2';
import React from 'react';
import LinkIconButtonV3 from 'Components/buttons/LinkIconButton/LinkIconButtonV3';
import { usePageHeaderContextV3 } from 'Source/contexts/pageHeaderContext';
import Portal from 'Components/utils/Portal';
import { slugify } from 'Helpers/utils';
import { useTheme } from '@mui/material';

interface PageHeaderComposition {
  Actions: React.FC<FlexProps>;
}

export const PAGE_HEADER_HEIGHT = 60;

/**
 * Shared portal target id used by `PageHeaderV3.Actions` and by every
 * shell that hosts a page header (top-app `HeaderV3`, settings shell).
 *
 * `LayoutWrapper` (routes.tsx) makes those shells mutually exclusive,
 * so the id only ever appears once in the DOM — but referencing this
 * constant from every renderer keeps the magic string in one place.
 */
export const PAGE_HEADER_ACTIONS_PORTAL_ID = 'main-header';

const Description = ({ description }: { description?: React.ReactNode }) => {
  const theme = useTheme();
  const { tokens } = theme.palette;

  if (description) {
    if (typeof description === 'string') {
      return (
        <Text color={tokens.content.text.neutral[2]} mt={2}>
          {description}
        </Text>
      );
    }
    if (React.isValidElement(description)) {
      return description;
    }
  }

  return null;
};

interface PageHeaderProps {
  wrapperProps?: FlexProps;
}

const PageHeaderV3: React.FC<PageHeaderProps> & PageHeaderComposition = () => {
  const { title, description, link, pageHeaderStyles } = usePageHeaderContextV3();
  const theme = useTheme();
  const { tokens } = theme.palette;

  if (!title) {
    return null;
  }

  return (
    <Flex
      direction="column"
      align="flex-start"
      justify="center"
      order={-1}
      data-testid="page-header"
      mr={4}
      height={PAGE_HEADER_HEIGHT}
      data-dd-privacy="mask-user-input"
      flex={1}
      {...pageHeaderStyles}
    >
      <Flex alignItems="center" width="100%">
        {React.isValidElement(title) ? (
          title
        ) : (
          <Heading
            variant="h4"
            fontWeight="semibold"
            color={tokens.content.text.on.color.light.dark}
          >
            {title}
          </Heading>
        )}
        {link && (
          <Tooltip
            content={
              <Text maxWidth={300}>
                View Panther documentation to learn more about features on this page
              </Text>
            }
          >
            <Box ml={2}>
              <LinkIconButtonV3
                data-tid={`help-link-${slugify(String(title))}`}
                icon="docs"
                size="small"
                variant="ghost"
                aria-label={`link to ${
                  React.isValidElement(title) ? '' : title
                } page documentation`}
                external
                to={link}
              />
            </Box>
          </Tooltip>
        )}
      </Flex>
      <Description description={description} />
    </Flex>
  );
};

const PageHeaderActions: React.FC<FlexProps> = ({ children, ...rest }) => {
  return (
    <Portal selector={`#${PAGE_HEADER_ACTIONS_PORTAL_ID}`}>
      <Flex
        {...rest}
        alignItems="center"
        wrap="nowrap"
        data-dd-privacy="mask-user-input"
        height={PAGE_HEADER_HEIGHT}
      >
        {children}
      </Flex>
    </Portal>
  );
};

PageHeaderV3.Actions = React.memo(PageHeaderActions);

export default PageHeaderV3;
